import type maplibregl from "maplibre-gl";
import { spotCoordinates } from "../../shared/quest-feed.ts";
import { fitRotated } from "../../shared/rotated-fit.ts";
import type { LngLat } from "../../shared/types.ts";
import {
  QUEST_SPOT_DOM_FIT_MAX_ZOOM,
  QUEST_SPOT_DOM_SINGLE_ZOOM,
} from "../../shared/quest-spot-map-fit.ts";

export interface ListSpotThumbPreview {
  lat: number;
  lng: number;
  kind?: string | null;
  title?: string | null;
  thumbnail_image?: string | null;
}

/** Photo circle diameter on 96×68 list thumbs (not full map 40px markers). */
export const LIST_THUMB_SPOT_PHOTO_PX = 22;

const LIST_THUMB_EDGE_PAD = 12;

async function decodeThumbBlob(blob: Blob, contentType: string): Promise<CanvasImageSource | null> {
  const mime = (blob.type || contentType.split(";")[0]?.trim() || "").toLowerCase();
  if (!mime.startsWith("image/")) return null;
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(blob);
    } catch {
      /* fall through */
    }
  }
  const objectUrl = URL.createObjectURL(blob);
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    img.src = objectUrl;
  });
}

async function loadSpotThumbImage(url: string | null | undefined): Promise<CanvasImageSource | null> {
  const trimmed = url?.trim() ?? "";
  if (!trimmed || typeof fetch === "undefined") return null;
  try {
    const res = await fetch(trimmed, { credentials: "same-origin", cache: "force-cache" });
    if (!res.ok) return null;
    const contentType = res.headers.get("content-type") ?? "";
    if (!contentType.startsWith("image/")) return null;
    return decodeThumbBlob(await res.blob(), contentType);
  } catch {
    return null;
  }
}

function drawPinFallback(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.55, 0, Math.PI * 2);
  ctx.fillStyle = "#8b5cf6";
  ctx.fill();
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = Math.max(1.5, r * 0.14);
  ctx.stroke();
}

function drawPhotoCircle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  img: CanvasImageSource | null,
): void {
  if (!img) {
    drawPinFallback(ctx, cx, cy, r);
    return;
  }
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(img, cx - r, cy - r, r * 2, r * 2);
  ctx.restore();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = Math.max(1.5, r * 0.14);
  ctx.stroke();
}

/** Camera for spot list thumbs: all located spots, inset for marker circles. */
export function listSpotThumbFitView(
  spots: readonly ListSpotThumbPreview[],
  w: number,
  h: number,
): { center: LngLat; zoom: number; bearing: number } | null {
  const points = spotCoordinates(spots);
  if (!points.length || w <= 0 || h <= 0) return null;
  const pad = LIST_THUMB_EDGE_PAD + LIST_THUMB_SPOT_PHOTO_PX / 2;
  if (points.length === 1) {
    return { center: points[0]!, zoom: QUEST_SPOT_DOM_SINGLE_ZOOM, bearing: 0 };
  }
  return fitRotated(points, 0, {
    width: w,
    height: h,
    padding: { top: pad, right: pad, bottom: pad, left: pad },
    maxZoom: QUEST_SPOT_DOM_FIT_MAX_ZOOM,
  });
}

/** Copy the MapLibre WebGL canvas and paint round spot photo markers at projected lng/lat. */
export async function snapshotMapWithSpotMarkers(
  map: maplibregl.Map,
  spots: readonly ListSpotThumbPreview[],
  photoCssPx = LIST_THUMB_SPOT_PHOTO_PX,
): Promise<string> {
  const src = map.getCanvas();
  const out = document.createElement("canvas");
  out.width = src.width;
  out.height = src.height;
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("2d context unavailable");
  ctx.drawImage(src, 0, 0);
  const scale = src.width / Math.max(map.getContainer().clientWidth, 1);
  const r = (photoCssPx / 2) * scale;
  const coords = spotCoordinates(spots);
  const byCoord = new Map<string, ListSpotThumbPreview>();
  for (const s of spots) {
    const key = `${s.lng},${s.lat}`;
    if (!byCoord.has(key)) byCoord.set(key, s);
  }
  const images = await Promise.all(
    coords.map(async (ll) => {
      const spot = byCoord.get(`${ll[0]},${ll[1]}`);
      return loadSpotThumbImage(spot?.thumbnail_image);
    }),
  );
  for (let i = 0; i < coords.length; i++) {
    const ll = coords[i]!;
    const p = map.project(ll);
    drawPhotoCircle(ctx, p.x * scale, p.y * scale, r, images[i] ?? null);
  }
  return out.toDataURL("image/jpeg", 0.86);
}
